/**
 * `store/player` — `spec.md` §13.9's player slice: "written by the engine's event
 * listeners, read by the UI".
 *
 * Three rules shape this file:
 *
 * 1. **It contains no `HTMLAudioElement` and imports nothing from `../engine`.** The
 *    element is a module-level singleton created outside React (§13.2); this slice only
 *    mirrors what the engine reports. `wiring.ts` (T37) is the one place the two meet.
 *
 * 2. **`isPlaying` mirrors the element, it does not predict it.** §13.6: "reflect the
 *    real `audio.paused` state in the UI rather than an optimistic local flag, and never
 *    assume `play()` succeeded because it did not throw." Telegram taking the OS audio
 *    session pauses us without asking; an optimistic flag would leave a spinning disc
 *    over silence. So there is a `setPlaying` setter fed by the `play`/`pause` events
 *    and no "we asked it to play" state.
 *
 * 3. **Duration is two numbers, not one.** `serverDurationS` is what the catalogue told
 *    us and is renderable the instant a track is selected; `durationMs` is what the
 *    element eventually reports (`NaN` before `loadedmetadata`, `Infinity` for an
 *    unbounded stream, and drifting for a VBR MP3 with no Xing header). §13.5 wants the
 *    server value on screen immediately so the UI never flickers `--:--`, which is what
 *    {@link selectEffectiveDurationMs} encodes.
 *
 * Every field is JSON-serialisable; the failure object is a plain record, never an
 * `Error`.
 */

import { create } from 'zustand';

import { ALL_FAILURE_CODES } from '../lib/api';
import type { ApiFailure } from '../lib/api';

/* ────────────────────────────────────────────────────────────────────────────
 * Playback failures
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * The media faults the engine's classifier (§13.6) can hand up, in `MediaError.code`
 * order followed by the two that have no numeric code.
 *
 * `MEDIA_ABORTED` (`MediaError.code === 1`) is deliberately absent: §13.6 says to ignore
 * it, because it means the user did something — a skip, a new `src` — and painting an
 * error plate over a deliberate action is a lie.
 */
export const PLAYER_ERROR_CODES = [
  /** `2 MEDIA_ERR_NETWORK`. Retryable on the backoff ladder. */
  'MEDIA_NETWORK',
  /** `3 MEDIA_ERR_DECODE` — how a truncated range response presents. */
  'MEDIA_DECODE',
  /** `4 MEDIA_ERR_SRC_NOT_SUPPORTED`. Do not retry the same URL; re-mint once. */
  'MEDIA_SRC',
  /** `el.error` was `null` inside an `error` handler. Treated as retryable network. */
  'MEDIA_UNKNOWN',
  /** `play()` rejected with `NotAllowedError`: the element needs a gesture. */
  'MEDIA_NEEDS_GESTURE',
  /** The watchdog saw 12 s with no `progress`/`timeupdate` while unpaused. */
  'MEDIA_STALLED',
] as const;

export type PlayerErrorCode = (typeof PLAYER_ERROR_CODES)[number];

const PLAYER_MESSAGE_RU: Readonly<Record<PlayerErrorCode, string>> = Object.freeze({
  MEDIA_NETWORK: 'Связь оборвалась на середине. Пробуем снова.',
  MEDIA_DECODE: 'Файл прочитался не полностью. Пробуем снова.',
  MEDIA_SRC: 'Эта запись не проигрывается.',
  MEDIA_UNKNOWN: 'Воспроизведение прервалось. Пробуем снова.',
  MEDIA_NEEDS_GESTURE: 'Нажмите воспроизведение.',
  MEDIA_STALLED: 'Звук встал. Пробуем снова.',
});

/** Only these are worth a `ЕЩЁ РАЗ` stamp; the rest are dead ends. */
const PLAYER_RETRYABLE: ReadonlySet<PlayerErrorCode> = new Set<PlayerErrorCode>([
  'MEDIA_NETWORK',
  'MEDIA_DECODE',
  'MEDIA_UNKNOWN',
  'MEDIA_STALLED',
]);

/**
 * Build the renderable failure for a media fault.
 *
 * It is structurally an {@link ApiFailure} so the one `ErrorState` component renders a
 * dead network and a dead decoder the same way. The mono catalogue code continues the
 * numbering of `lib/api.ts` rather than restarting it, so no two faults in the app ever
 * print the same `ERR 0x..`.
 */
export function playerFailure(code: PlayerErrorCode): ApiFailure {
  const offset = ALL_FAILURE_CODES.length + PLAYER_ERROR_CODES.indexOf(code);
  return {
    code: 'INTERNAL',
    message: PLAYER_MESSAGE_RU[code],
    displayCode: `ERR 0x${offset.toString(16).toUpperCase().padStart(2, '0')}`,
    status: 0,
    retryable: PLAYER_RETRYABLE.has(code),
    details: { player: code },
  };
}

/* ────────────────────────────────────────────────────────────────────────────
 * State
 * ──────────────────────────────────────────────────────────────────────────── */

export interface PlayerData {
  /** The track the element is loaded with, or `null` before the first selection. */
  trackId: number | null;
  /** `tracks.duration_s` from the catalogue row. Renders before any metadata (§13.5). */
  serverDurationS: number;
  /** The real element state, mirrored from `play`/`pause` (see rule 2 above). */
  isPlaying: boolean;
  positionMs: number;
  /** From `durationchange`; `0` while the element has nothing finite to report. */
  durationMs: number;
  /** Seconds of contiguous buffer ahead of the playhead. */
  bufferedAheadS: number;
  isBuffering: boolean;
  /** `true` between `pointerdown` and `pointerup` on the scrub band. */
  isSeeking: boolean;
  /** `true` once a gesture-driven `play()` has cleared this element's lock (§13.2). */
  unlocked: boolean;
  /** `true` when the last `play()` was refused for want of a gesture. */
  needsGesture: boolean;
  playbackRate: number;
  volume: number;
  /** §13.5: `seekable.end(last) >= duration - 0.5`. `false` degrades the scrub bar. */
  isFullySeekable: boolean;
  /** How many recovery attempts the current fault has consumed (§13.6's ladder). */
  recoveryAttempt: number;
  error: ApiFailure | null;
}

export interface PlayerActions {
  /** A new track is loading. Clears position, duration, buffer and error. */
  setTrack(trackId: number | null, serverDurationS?: number): void;
  setPlaying(isPlaying: boolean): void;
  setPosition(positionMs: number): void;
  /** From `durationchange`. A non-finite or non-positive value is ignored, not stored. */
  setDuration(durationMs: number): void;
  setBufferedAhead(seconds: number): void;
  setBuffering(isBuffering: boolean): void;
  setSeeking(isSeeking: boolean): void;
  setUnlocked(unlocked: boolean): void;
  setNeedsGesture(needsGesture: boolean): void;
  setPlaybackRate(rate: number): void;
  setVolume(volume: number): void;
  setFullySeekable(isFullySeekable: boolean): void;
  /** `null` clears the plate and resets the recovery ladder. */
  setError(code: PlayerErrorCode | null): void;
  noteRecoveryAttempt(): number;
  reset(): void;
}

export type PlayerState = PlayerData & PlayerActions;

const EMPTY: PlayerData = {
  trackId: null,
  serverDurationS: 0,
  isPlaying: false,
  positionMs: 0,
  durationMs: 0,
  bufferedAheadS: 0,
  isBuffering: false,
  isSeeking: false,
  unlocked: false,
  needsGesture: false,
  playbackRate: 1,
  volume: 1,
  isFullySeekable: true,
  recoveryAttempt: 0,
  error: null,
};

export function createPlayerStore() {
  return create<PlayerState>()((set, get) => ({
    ...EMPTY,

    setTrack(trackId, serverDurationS = 0): void {
      set({
        trackId,
        serverDurationS: Number.isFinite(serverDurationS) ? Math.max(0, serverDurationS) : 0,
        positionMs: 0,
        durationMs: 0,
        bufferedAheadS: 0,
        isBuffering: trackId !== null,
        isSeeking: false,
        recoveryAttempt: 0,
        error: null,
      });
    },

    setPlaying(isPlaying): void {
      // A real `play` event is proof the element is unlocked, and proof that whatever
      // gesture prompt was on screen can go.
      set(isPlaying ? { isPlaying: true, unlocked: true, needsGesture: false } : { isPlaying });
    },

    setPosition(positionMs): void {
      if (!Number.isFinite(positionMs)) return;
      set({ positionMs: Math.max(0, Math.round(positionMs)) });
    },

    setDuration(durationMs): void {
      // `NaN` before `loadedmetadata` and `Infinity` for an unbounded resource (§13.5).
      // Storing either would propagate into `setPositionState`, which throws on both.
      if (!Number.isFinite(durationMs) || durationMs <= 0) return;
      set({ durationMs: Math.round(durationMs) });
    },

    setBufferedAhead(seconds): void {
      if (!Number.isFinite(seconds)) return;
      set({ bufferedAheadS: Math.max(0, seconds) });
    },

    setBuffering(isBuffering): void {
      set({ isBuffering });
    },
    setSeeking(isSeeking): void {
      set({ isSeeking });
    },
    setUnlocked(unlocked): void {
      set({ unlocked });
    },
    setNeedsGesture(needsGesture): void {
      set({ needsGesture });
    },
    setPlaybackRate(rate): void {
      // `setPositionState` throws on `playbackRate: 0`, so a zero never enters state.
      if (!Number.isFinite(rate) || rate <= 0) return;
      set({ playbackRate: rate });
    },
    setVolume(volume): void {
      if (!Number.isFinite(volume)) return;
      set({ volume: Math.min(1, Math.max(0, volume)) });
    },
    setFullySeekable(isFullySeekable): void {
      set({ isFullySeekable });
    },

    setError(code): void {
      if (code === null) {
        set({ error: null, recoveryAttempt: 0 });
        return;
      }
      set({
        error: playerFailure(code),
        needsGesture: code === 'MEDIA_NEEDS_GESTURE' ? true : get().needsGesture,
      });
    },

    noteRecoveryAttempt(): number {
      const next = get().recoveryAttempt + 1;
      set({ recoveryAttempt: next });
      return next;
    },

    reset(): void {
      // `unlocked` deliberately survives a reset: the element's playback restriction is
      // cleared once per element for the app's lifetime (§13.2), and re-showing the
      // "tap to start" affordance after a track change would be a lie about the state
      // the browser is actually in.
      set({ ...EMPTY, unlocked: get().unlocked });
    },
  }));
}

/** The app-wide player slice. */
export const usePlayerStore = createPlayerStore();

/* ────────────────────────────────────────────────────────────────────────────
 * Selectors
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * §13.5's `effectiveDuration`, in milliseconds: the element's value once it is finite
 * and positive, the server's until then, `0` when neither is known.
 */
export const selectEffectiveDurationMs = (state: PlayerData): number => {
  if (state.durationMs > 0) return state.durationMs;
  return state.serverDurationS > 0 ? Math.round(state.serverDurationS * 1000) : 0;
};

/** `0..1`, clamped. `0` while the duration is unknown — never `NaN`, never a guess. */
export const selectProgressFraction = (state: PlayerData): number => {
  const duration = selectEffectiveDurationMs(state);
  if (duration <= 0) return 0;
  return Math.min(1, Math.max(0, state.positionMs / duration));
};

export const selectRemainingMs = (state: PlayerData): number =>
  Math.max(0, selectEffectiveDurationMs(state) - state.positionMs);

/** `true` when the disc should be spinning: playing, and not stalled behind a fault. */
export const selectIsSpinning = (state: PlayerData): boolean =>
  state.isPlaying && state.error === null;

/** `true` when the scrub bar may be dragged past the buffer (§13.5's honesty rule). */
export const selectCanScrubFreely = (state: PlayerData): boolean =>
  state.isFullySeekable && selectEffectiveDurationMs(state) > 0;

/** The persistence pointer this slice contributes (§13.7). */
export const selectResumePointer = (
  state: PlayerData,
): { trackId: number | null; positionMs: number } => ({
  trackId: state.trackId,
  positionMs: state.positionMs,
});
